import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Toast from '../../Shared/Toast';
import { useMercenary } from '../../../contexts/MercenaryContext';
import styles from './ViewTournaments.module.css';
import { formatDateTime } from '../../../utils/dateFormat';

interface Tournament {
  id: number;
  name: string;
  description: string;
  type: string;
  status: string;
  legend?: boolean;
  fill_with_beasts?: boolean;
  min_level: number;
  max_level: number;
  num_teams: number;
  race_requirement: string;
  team_size: number;
  participantCount: number;
  availableSpots: number;
  start_time: string;
  completed_at?: string;
  updatedAt: string;
  userParticipating?: boolean;
  seasonId?: number | null;
}

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  locked: 'Locked',
  ongoing: 'Ongoing',
  completed: 'Completed',
};

const TOURNAMENT_FILTERS_KEY = 'tournament_listFilters';

const loadFilters = () => {
  try {
    const saved = localStorage.getItem(TOURNAMENT_FILTERS_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return { filterMatchLevel: false, filterType: 'all', filterPve: 'all' };
};

const ViewTournaments: React.FC = () => {
  const navigate = useNavigate();
  const { mercenaryId, mercenary } = useMercenary();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'finished'>('upcoming');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [currentSeasonId, setCurrentSeasonId] = useState<number | null>(null);

  // Filters, persisted to localStorage
  const [filterMatchLevel, setFilterMatchLevel] = useState<boolean>(() => loadFilters().filterMatchLevel);
  const [filterType, setFilterType] = useState<'all' | 'normal' | 'death'>(() => loadFilters().filterType);
  const [filterPve, setFilterPve] = useState<'all' | 'fill' | 'nofill'>(() => loadFilters().filterPve);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  useEffect(() => {
    localStorage.setItem(TOURNAMENT_FILTERS_KEY, JSON.stringify({ filterMatchLevel, filterType, filterPve }));
  }, [filterMatchLevel, filterType, filterPve]);

  useEffect(() => {
    axios.get('/api/seasons/current', { withCredentials: true })
      .then(res => {
        const seasonId = res.data?.season?.id ?? null;
        setCurrentSeasonId(seasonId);
      })
      .catch(() => { /* offseason or error — leave currentSeasonId null */ });
  }, []);

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const params = mercenaryId ? { mercenaryId } : {};
        const response = await axios.get('/api/tournament', { params, withCredentials: true });
        setTournaments(response.data);
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        displayToast('Failed to load tournaments. Please try again later.', 'error');
      }
    };
    fetchTournaments();
  }, [mercenaryId]);

  const upcomingAndOngoing = tournaments
    .filter((t) => ['scheduled', 'locked', 'ongoing'].includes(t.status))
    .filter((t) => {
      if (filterMatchLevel && mercenary) {
        const lvl = (mercenary as any).level ?? 0;
        if (lvl < t.min_level || lvl > t.max_level) return false;
      }
      if (filterType === 'normal' && t.type !== 'normal') return false;
      if (filterType === 'death' && t.type !== 'death') return false;
      if (filterPve === 'fill' && !t.fill_with_beasts) return false;
      if (filterPve === 'nofill' && t.fill_with_beasts) return false;
      return true;
    });

  const finishedTournaments = tournaments
    .filter((t) => t.status === 'completed')
    .filter((t) => currentSeasonId === null || t.seasonId === currentSeasonId)
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

  const renderCards = (list: Tournament[], hideStatus = false) => {
    if (list.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>&#9876;</div>
          <p>No tournaments found.</p>
        </div>
      );
    }

    return (
      <div className={styles.cardGrid}>
        {list.map((t) => {
          const totalSlots = t.num_teams * t.team_size;
          const filled = totalSlots - t.availableSpots;
          const fillPct = totalSlots > 0 ? Math.round((filled / totalSlots) * 100) : 0;
          const startDate = new Date(t.start_time);
          const isDeath = t.type === 'death';

          return (
            <div
              key={t.id}
              className={`${styles.card} ${isDeath ? styles.deathCard : ''} ${t.legend ? styles.legendCard : ''} ${t.userParticipating ? styles.enrolledCard : ''}`}
              onClick={() => navigate(`/tournament/${t.id}`)}
            >
              {/* Badges row */}
              <div className={styles.badgeRow}>
                {t.legend && <span className={styles.legendBadge}>&#9812; LEGEND</span>}
                <span className={`${styles.typeBadge} ${isDeath ? styles.deathBadge : styles.normalBadge}`}>
                  {isDeath ? 'DEATH MATCH' : 'NORMAL'}
                </span>
                {t.userParticipating && (
                  <span className={styles.enrolledBadge}>&#10003; Enrolled</span>
                )}
                <span className={`${styles.fillBadge} ${t.fill_with_beasts ? styles.fillOn : styles.fillOff}`}>
                  {t.fill_with_beasts ? '✦ PvE Fill' : '✕ Players Only'}
                </span>
              </div>
              {!hideStatus && (
                <div className={styles.statusRow}>
                  <span className={`${styles.statusBadge} ${styles[`status_${t.status}`]}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                </div>
              )}

              {/* Title */}
              <h3 className={styles.cardTitle}>{t.name}</h3>

              {/* Meta row */}
              <div className={styles.cardMeta}>
                <span className={styles.metaPill}>
                  Lvl {t.min_level}–{t.max_level}
                </span>
                {t.team_size > 1 && (
                  <span className={styles.metaPill}>
                    {t.team_size}v{t.team_size}
                  </span>
                )}
                {t.race_requirement && t.race_requirement !== 'All' && (
                  <span className={styles.metaPill}>{t.race_requirement}</span>
                )}
              </div>

              {/* Time */}
              <div className={styles.cardTime}>
                {formatDateTime(startDate)}
              </div>

              {/* Signup deadline, only relevant before locking */}
              {t.status === 'scheduled' && (() => {
                const closeTime = new Date(startDate.getTime() - 60 * 60 * 1000);
                const isPast = closeTime <= new Date();
                return (
                  <div className={styles.signupClose} style={{ fontSize: '0.78rem', color: isPast ? 'var(--color-status-critical, #e07070)' : 'var(--color-text-secondary, #aaa)', marginTop: 4 }}>
                    {isPast ? 'Signup closed' : `Signup closes ${formatDateTime(closeTime)}`}
                  </div>
                );
              })()}

              {/* Participant bar */}
              <div className={styles.slotSection}>
                <div className={styles.slotLabel}>
                  <span>{filled} / {totalSlots} participants</span>
                  <span>{t.availableSpots} open</span>
                </div>
                <div className={styles.slotTrack}>
                  <div
                    className={`${styles.slotFill} ${fillPct >= 100 ? styles.slotFull : ''}`}
                    style={{ width: `${fillPct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}

      <div className={styles.header}>
        <h2 className={styles.title}>Tournaments</h2>
        <p className={styles.subtitle}>Combat, glory, and riches await the worthy</p>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'upcoming' ? styles.active : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          Upcoming &amp; Ongoing
          {upcomingAndOngoing.length > 0 && (
            <span className={styles.tabCount}>{upcomingAndOngoing.length}</span>
          )}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'finished' ? styles.active : ''}`}
          onClick={() => setActiveTab('finished')}
        >
          Past Tournaments
        </button>
      </div>

      {activeTab === 'upcoming' && (
        <div className={styles.filterBar}>
          <button
            className={`${styles.filterBtn} ${filterMatchLevel ? styles.filterActive : ''}`}
            onClick={() => setFilterMatchLevel(v => !v)}
            title={mercenary ? `Your level: ${(mercenary as any).level}` : 'Level filter'}
          >
            My Level
          </button>
          <button
            className={`${styles.filterBtn} ${filterType === 'normal' ? styles.filterActive : ''}`}
            onClick={() => setFilterType(v => v === 'normal' ? 'all' : 'normal')}
          >
            Normal
          </button>
          <button
            className={`${styles.filterBtn} ${filterType === 'death' ? styles.filterActive : ''}`}
            onClick={() => setFilterType(v => v === 'death' ? 'all' : 'death')}
          >
            Death
          </button>
          <button
            className={`${styles.filterBtn} ${filterPve === 'fill' ? styles.filterActive : ''}`}
            onClick={() => setFilterPve(v => v === 'fill' ? 'all' : 'fill')}
          >
            ✦ PvE Fill
          </button>
          <button
            className={`${styles.filterBtn} ${filterPve === 'nofill' ? styles.filterActive : ''}`}
            onClick={() => setFilterPve(v => v === 'nofill' ? 'all' : 'nofill')}
          >
            ✕ Players Only
          </button>
        </div>
      )}

      <div className={styles.content}>
        {activeTab === 'upcoming' && renderCards(upcomingAndOngoing)}
        {activeTab === 'finished' && renderCards(finishedTournaments, true)}
      </div>
    </div>
  );
};

export default ViewTournaments;
